import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Send, Sparkles, HeartHandshake, Lock, Zap, MoreHorizontal, FileText, Dices, AlertTriangle, Eye, Shuffle, Flag, ShieldCheck } from "lucide-react";
import { api, photoUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useApiErrorHandler, usePaywall } from "@/contexts/PaywallContext";
import VerifiedBadge from "@/components/VerifiedBadge";
import ThisOrThatModal from "@/components/ThisOrThatModal";
import ContractModal from "@/components/ContractModal";
import ReportModal from "@/components/ReportModal";
import VerificationModal from "@/components/VerificationModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function toAbsolute(url) {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    if (url.startsWith("/api/photos/")) {
        return photoUrl(url.replace("/api/photos/", ""));
    }
    return `${BACKEND_URL}${url}`;
}

function formatTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function ChatRoom() {
    const { matchId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const handleApiError = useApiErrorHandler();
    const { openPaywall, status } = usePaywall();
    const isPremium = !!status?.premium;
    const isVerified = !!user?.verified;
    const [vStatusLocal, setVStatusLocal] = useState(null);
    const vStatus = vStatusLocal || user?.verification_status || (user?.verified ? "approved" : null);
    const [messages, setMessages] = useState([]);
    const [other, setOther] = useState(null);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const [canNudge, setCanNudge] = useState(false);
    const [nudgeReason, setNudgeReason] = useState(null);
    const [nudging, setNudging] = useState(false);
    const [totOpen, setTotOpen] = useState(false);
    const [contractOpen, setContractOpen] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const [verifyOpen, setVerifyOpen] = useState(false);
    const [revealedIds, setRevealedIds] = useState(new Set());
    const [icebreakers, setIcebreakers] = useState([]);
    const [iceLoading, setIceLoading] = useState(false);
    const inputRef = useRef(null);
    const listEndRef = useRef(null);
    const pollRef = useRef(null);
    const lastCreatedRef = useRef(null);

    const scrollDown = useCallback(() => {
        setTimeout(() => listEndRef.current?.scrollIntoView({ behavior: "smooth" }), 40);
    }, []);

    const fetchIcebreakers = useCallback(
        (refresh = false) => {
            setIceLoading(true);
            api.get(`/chats/${matchId}/icebreakers`, { params: refresh ? { refresh: true } : {} })
                .then(({ data: ice }) => setIcebreakers(ice.icebreakers || []))
                .catch(() => {})
                .finally(() => setIceLoading(false));
        },
        [matchId],
    );

    const load = useCallback(
        async (initial = false) => {
            try {
                const params = {};
                if (!initial && lastCreatedRef.current) params.since = lastCreatedRef.current;
                const { data } = await api.get(`/chats/${matchId}/messages`, { params });
                if (initial) {
                    setMessages(data.messages || []);
                    setOther(data.match?.user || null);
                    setCanNudge(!!data.can_nudge);
                    setNudgeReason(data.nudge_reason || null);
                    if (data.messages?.length) {
                        lastCreatedRef.current = data.messages[data.messages.length - 1].created_at;
                    }
                    // Mark read
                    api.post(`/chats/${matchId}/read`).catch(() => {});
                    setLoading(false);
                    scrollDown();
                    // Fetch icebreakers only for a fresh, empty conversation
                    if (!(data.messages || []).length) {
                        fetchIcebreakers(false);
                    }
                } else if (data.messages?.length) {
                    setMessages((prev) => {
                        const seen = new Set(prev.map((m) => m.id));
                        const news = data.messages.filter((m) => !seen.has(m.id));
                        if (!news.length) return prev;
                        lastCreatedRef.current = news[news.length - 1].created_at;
                        return [...prev, ...news];
                    });
                    // Any inbound → mark read
                    if (data.messages.some((m) => m.to_user_id === user?.user_id)) {
                        api.post(`/chats/${matchId}/read`).catch(() => {});
                    }
                    scrollDown();
                }
                // Always update nudge eligibility (their reply invalidates it)
                setCanNudge(!!data.can_nudge);
                setNudgeReason(data.nudge_reason || null);
            } catch (e) {
                if (initial) {
                    toast.error("Couldn't load chat");
                    navigate("/app/chat");
                }
            }
        },
        [matchId, navigate, user?.user_id, scrollDown, fetchIcebreakers],
    );

    useEffect(() => {
        load(true);
        pollRef.current = setInterval(() => load(false), 3000);
        return () => clearInterval(pollRef.current);
    }, [load]);

    const send = async (e) => {
        e.preventDefault();
        const body = text.trim();
        if (!body || sending) return;
        setSending(true);
        // Optimistic
        const optimistic = {
            id: `tmp-${Date.now()}`,
            match_id: matchId,
            from_user_id: user.user_id,
            to_user_id: other?.user_id,
            body,
            created_at: new Date().toISOString(),
            read_by_recipient: false,
            _optimistic: true,
        };
        setMessages((m) => [...m, optimistic]);
        setText("");
        scrollDown();
        try {
            const { data } = await api.post(`/chats/${matchId}/messages`, { body });
            setMessages((prev) => prev.map((m) => (m.id === optimistic.id ? data : m)));
            lastCreatedRef.current = data.created_at;
        } catch (err) {
            setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
            if (!handleApiError(err)) {
                toast.error("Message not sent");
            }
        } finally {
            setSending(false);
        }
    };

    const avatar = other?.picture || (other?.photo_urls?.[0] ? toAbsolute(other.photo_urls[0]) : null);

    return (
        <div className="fixed inset-0 z-40 bg-background flex flex-col" data-testid="chat-room">
            {/* Header */}
            <header className="flex items-center gap-3 border-b border-border bg-card/80 backdrop-blur px-4 py-3 pt-4">
                <button
                    onClick={() => navigate("/app/chat")}
                    data-testid="chat-back-btn"
                    className="h-10 w-10 rounded-full hover:bg-accent grid place-items-center lift"
                    aria-label="Back"
                >
                    <ArrowLeft className="h-5 w-5" strokeWidth={2.5} />
                </button>
                <div className="h-11 w-11 rounded-full overflow-hidden bg-accent grid place-items-center text-accent-foreground font-black">
                    {avatar ? (
                        <img src={avatar} alt={other?.name} className="h-full w-full object-cover" />
                    ) : (
                        (other?.name || "?").charAt(0).toUpperCase()
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="font-display font-bold truncate flex items-center gap-1" data-testid="chat-header-name">
                        {other?.name || "…"}
                        <VerifiedBadge verified={other?.verified} size="sm" />
                    </div>
                    {other?.match_percent ? (
                        <div className="flex items-center gap-1 text-xs text-primary font-semibold">
                            <Sparkles className="h-3 w-3" strokeWidth={3} /> {other.match_percent}% match
                        </div>
                    ) : null}
                </div>
                {/* Action icons */}
                <div className="flex items-center gap-1 flex-shrink-0">
                    {isPremium && (
                        <>
                            <button
                                onClick={() => setTotOpen(true)}
                                data-testid="chat-tot-btn"
                                title="Play This-or-That"
                                className="h-10 w-10 rounded-full hover:bg-accent grid place-items-center lift"
                            >
                                <Dices className="h-5 w-5 text-primary" strokeWidth={2.5} />
                            </button>
                            <button
                                onClick={() => setContractOpen(true)}
                                data-testid="chat-contract-btn"
                                title="Roommate contract"
                                className="h-10 w-10 rounded-full hover:bg-accent grid place-items-center lift"
                            >
                                <FileText className="h-5 w-5 text-primary" strokeWidth={2.5} />
                            </button>
                        </>
                    )}
                    <button
                        onClick={() => setReportOpen(true)}
                        data-testid="chat-report-btn"
                        title="Report this person"
                        className="h-10 w-10 rounded-full hover:bg-destructive/10 grid place-items-center lift"
                    >
                        <Flag className="h-5 w-5 text-muted-foreground hover:text-destructive" strokeWidth={2.5} />
                    </button>
                </div>
            </header>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2" data-testid="chat-messages">
                {loading ? (
                    <div className="text-center text-sm text-muted-foreground pt-10">Loading messages…</div>
                ) : messages.length === 0 ? (
                    <div className="mt-10 mx-auto max-w-xs text-center">
                        <div className="mx-auto h-14 w-14 rounded-2xl bg-accent grid place-items-center text-accent-foreground">
                            <Sparkles className="h-7 w-7" strokeWidth={2.5} />
                        </div>
                        <div className="mt-4 font-display font-bold">Say hi to {other?.name || "them"}</div>
                        <p className="text-sm text-muted-foreground mt-1">Break the ice — ask about their move-in date, city, or favourite takeout.</p>
                        {isVerified && (iceLoading || icebreakers.length > 0) && (
                            <div className="mt-6" data-testid="chat-icebreakers">
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <div className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-1">
                                        <Sparkles className="h-3 w-3" strokeWidth={3} /> icebreakers
                                    </div>
                                    <button
                                        onClick={() => fetchIcebreakers(true)}
                                        disabled={iceLoading}
                                        data-testid="chat-icebreaker-shuffle"
                                        title="Shuffle suggestions"
                                        className="h-7 w-7 rounded-full grid place-items-center border border-border bg-card hover:border-primary hover:text-primary transition-colors duration-200 disabled:opacity-40 lift"
                                    >
                                        <Shuffle className={`h-3.5 w-3.5 ${iceLoading ? "animate-spin" : ""}`} strokeWidth={2.8} />
                                    </button>
                                </div>
                                {iceLoading ? (
                                    <div className="space-y-2">
                                        {[0, 1, 2].map((i) => (
                                            <div key={i} className="h-10 rounded-2xl bg-accent/60 animate-pulse" />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {icebreakers.map((ice, i) => (
                                            <button
                                                key={i}
                                                data-testid={`chat-icebreaker-${i}`}
                                                onClick={() => {
                                                    setText(ice);
                                                    inputRef.current?.focus();
                                                }}
                                                className="w-full text-left text-sm rounded-2xl border border-border bg-card px-4 py-2.5 hover:border-primary hover:bg-accent/50 transition-colors duration-200 lift"
                                            >
                                                {ice}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <div className="text-[10px] text-muted-foreground mt-2">Tap one to drop it in the box, then tweak &amp; send.</div>
                            </div>
                        )}
                    </div>
                ) : (
                    <AnimatePresence initial={false}>
                        {messages.map((m, idx) => {
                            const mine = m.from_user_id === user?.user_id;
                            // Find last own message index for "Seen" indicator
                            const isLastMine =
                                mine &&
                                idx === messages.map((mm, i) => (mm.from_user_id === user?.user_id ? i : -1)).filter((i) => i >= 0).pop();
                            const isNudge = !!m.is_nudge;
                            // Flagged inbound message: blur unless recipient chose to reveal
                            const isFlagged = !mine && !!m.flagged;
                            const isRevealed = revealedIds.has(m.id);
                            return (
                                <motion.div
                                    key={m.id}
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className={`flex ${mine ? "justify-end" : "justify-start"}`}
                                >
                                    <div className={`max-w-[75%] ${isNudge ? "w-full sm:w-auto" : ""}`}>
                                        {isNudge && (
                                            <div className={`flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-primary mb-1 ${mine ? "justify-end" : "justify-start"}`}>
                                                <HeartHandshake className="h-3 w-3" strokeWidth={3} /> nudge
                                            </div>
                                        )}
                                        {isFlagged && !isRevealed ? (
                                            <div
                                                data-testid={`chat-msg-flagged-${m.id}`}
                                                className="rounded-3xl px-4 py-3 border-2 border-destructive bg-destructive/10 rounded-bl-md"
                                            >
                                                <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-destructive mb-1">
                                                    <AlertTriangle className="h-3 w-3" strokeWidth={3} /> flagged
                                                </div>
                                                <div className="text-sm text-muted-foreground mb-2">
                                                    This message was flagged as potentially inappropriate.
                                                </div>
                                                <button
                                                    onClick={() => setRevealedIds((s) => new Set(s).add(m.id))}
                                                    data-testid={`chat-msg-reveal-btn-${m.id}`}
                                                    className="h-8 rounded-full bg-destructive text-white text-[10px] font-black uppercase tracking-widest px-3 lift flex items-center gap-1"
                                                >
                                                    <Eye className="h-3 w-3" strokeWidth={3} /> Show anyway
                                                </button>
                                            </div>
                                        ) : (
                                            <div
                                                data-testid={`chat-msg-${mine ? "mine" : "theirs"}`}
                                                className={`rounded-3xl px-4 py-2.5 text-sm leading-relaxed ${
                                                    mine
                                                        ? "bg-primary text-primary-foreground rounded-br-md"
                                                        : "bg-card border border-border rounded-bl-md"
                                                } ${isNudge ? "border-2 border-primary" : ""} ${isRevealed ? "border-2 border-destructive" : ""}`}
                                            >
                                                <div>{m.body}</div>
                                                <div className={`mt-1 text-[10px] opacity-70 ${mine ? "text-right" : "text-left"}`}>
                                                    {formatTime(m.created_at)}
                                                </div>
                                            </div>
                                        )}
                                        {isLastMine && (
                                            <div
                                                className="mt-1 text-[10px] font-semibold text-muted-foreground text-right pr-1"
                                                data-testid="chat-seen-indicator"
                                            >
                                                {m.read_by_recipient ? "Seen ✓" : "Sent"}
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>
                )}
                <div ref={listEndRef} />
            </div>

            {/* Ghost nudge banner */}
            {canNudge && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="border-t border-border bg-accent px-3 py-2 flex items-center gap-2"
                    data-testid="nudge-banner"
                >
                    <div className="min-w-0 flex-1 text-xs">
                        <div className="font-black uppercase tracking-widest text-[10px] text-primary">Ghost check 💀</div>
                        <div className="text-foreground/80 truncate">
                            {other?.name || "They"} went quiet ({nudgeReason || "3+ days"}). Send a soft nudge?
                        </div>
                    </div>
                    <button
                        onClick={async () => {
                            if (nudging) return;
                            setNudging(true);
                            try {
                                const { data } = await api.post(`/chats/${matchId}/nudge`);
                                setMessages((prev) => [...prev, data.message]);
                                setCanNudge(false);
                                lastCreatedRef.current = data.message.created_at;
                                scrollDown();
                                toast.success("Nudge sent 🫶");
                            } catch (e) {
                                toast.error(e?.response?.data?.detail || "Couldn't send nudge");
                            } finally {
                                setNudging(false);
                            }
                        }}
                        data-testid="nudge-send-btn"
                        disabled={nudging}
                        className="h-9 rounded-full bg-primary text-primary-foreground px-4 text-xs font-black uppercase tracking-widest lift disabled:opacity-50 flex items-center gap-1"
                    >
                        <HeartHandshake className="h-3.5 w-3.5" strokeWidth={3} />
                        Nudge
                    </button>
                </motion.div>
            )}

            {/* Composer OR verify-to-chat gate */}
            {!isVerified ? (
                <button
                    type="button"
                    onClick={() => setVerifyOpen(true)}
                    data-testid="chat-verify-gate"
                    className="border-t-2 border-foreground bg-primary text-primary-foreground px-4 py-4 flex items-center gap-3 w-full text-left lift"
                >
                    <div className="h-11 w-11 rounded-2xl bg-white/20 grid place-items-center flex-shrink-0">
                        <ShieldCheck className="h-5 w-5" strokeWidth={2.8} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-display font-black text-base leading-tight">
                            {vStatus === "pending" ? "Verification in review" : "Get verified to chat"}
                        </div>
                        <div className="text-xs opacity-90">
                            {vStatus === "pending"
                                ? "We're reviewing your selfie — you can chat once approved."
                                : vStatus === "rejected"
                                    ? "Your last try didn't pass. Tap to record again."
                                    : "A 60-sec selfie check keeps Flatch safe. Tap to verify."}
                        </div>
                    </div>
                    <div className="h-11 rounded-2xl bg-white text-primary font-black px-4 grid place-items-center flex-shrink-0">
                        <Zap className="h-4 w-4" fill="currentColor" strokeWidth={0} />
                    </div>
                </button>
            ) : (
                <form onSubmit={send} className="border-t border-border bg-card/90 backdrop-blur px-3 py-3 flex items-center gap-2">
                    <input
                        ref={inputRef}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Message…"
                        data-testid="chat-input"
                        className="flex-1 h-12 rounded-2xl border border-border bg-background px-4 outline-none focus:border-primary text-sm"
                    />
                    <button
                        type="submit"
                        disabled={!text.trim() || sending}
                        data-testid="chat-send-btn"
                        className="h-12 w-12 rounded-2xl bg-primary text-primary-foreground grid place-items-center lift disabled:opacity-40"
                    >
                        <Send className="h-5 w-5" strokeWidth={2.5} />
                    </button>
                </form>
            )}

            <ThisOrThatModal
                open={totOpen}
                matchId={matchId}
                otherName={other?.name}
                onClose={() => setTotOpen(false)}
            />
            <ContractModal
                open={contractOpen}
                matchId={matchId}
                otherName={other?.name}
                currentUserName={user?.name}
                onClose={() => setContractOpen(false)}
            />
            <ReportModal
                open={reportOpen}
                reportedUserId={other?.user_id}
                reportedName={other?.name}
                matchId={matchId}
                onClose={() => setReportOpen(false)}
            />
            <VerificationModal
                open={verifyOpen}
                onClose={() => setVerifyOpen(false)}
                onVerified={() => setVStatusLocal("pending")}
            />
        </div>
    );
}
